<template>
  <div class="ic-camera" v-show="show">
    <div class="ic-camera--header">
      <button @click="closeCamera">
        <AngleLeftIcon />
      </button>
      <button @click="openDocument">
        <FolderIcon />
      </button>
    </div>
    <div class="ic-camera--container">
      <video ref="video" autoplay playsinline style="width:100%;height:100%;object-fit:cover;"></video>
      <canvas ref="canvas" style="display:none;"></canvas>
    </div>
    <div class="ic-camera--footer">
      <div class="ic-camera-sources select-source">
        <select v-model="curCamera" @change="onChange">
          <option v-for="source in sources" :key="source.deviceId" :value="source.deviceId">{{ source.label }}</option>
        </select>
      </div>
      <button class="capture" @click="captureImage">
        <CameraIcon />
      </button>
    </div>
    <div v-if="permissionError" class="ic-camera--permission-error">
      <p>{{ $t('message.FileUploadComponent.cameraComponent.permissionDenied') }}</p>
    </div>
    <loader v-if="loading" />
  </div>
</template>
<script>
import Loader from './Loader.vue'
import FolderIcon from './Icons/Folder.vue'
import CameraIcon from './Icons/Camera.vue'
import AngleLeftIcon from './Icons/AngleLeft.vue'
export default {
  name: 'ImageCaptureCamera',
  components: {
    Loader,
    FolderIcon,
    CameraIcon,
    AngleLeftIcon
  },
  inject: ['loadImage', 'loadImageFromFile'],
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      curCamera: null,
      loading: true,
      sources: [],
      stream: null,
      permissionError: false
    }
  },
  watch: {
    show(value) {
      if (value) {
        this.startStream()
      } else {
        this.stopStream()
      }
    }
  },
  async mounted() {
    await this.enumerateSources()
    if (this.show) await this.startStream()
  },
  beforeDestroy() {
    this.stopStream()
  },
  methods: {
    async enumerateSources() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(d => d.kind === 'videoinput')
        if (videoDevices.some(d => d.deviceId !== '')) {
          this.sources = videoDevices
        }
      } catch (e) {
        this.$emit('camera-error', e)
      }
    },
    async startStream() {
      this.loading = true
      this.stopStream()
      try {
        document.body.style.overflow = 'hidden'
        const videoConstraints = this.curCamera
          ? { deviceId: { exact: this.curCamera } }
          : { facingMode: 'environment' }
        const constraints = {
          video: {
            ...videoConstraints,
            width: { ideal: 9999 },
            height: { ideal: 9999 }
          }
        }
        let stream
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints)
        } catch (constraintErr) {
          if (
            constraintErr.name === 'OverconstrainedError' ||
            constraintErr.name === 'ConstraintNotSatisfiedError' ||
            constraintErr.name === 'NotReadableError'
          ) {
            // Retry without resolution constraints
            // OverconstrainedError/ConstraintNotSatisfiedError: front camera can't satisfy width/height:9999
            // NotReadableError: iOS Safari hardware busy when switching cameras
            stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints })
          } else {
            throw constraintErr
          }
        }
        this.stream = stream
        this.$refs.video.srcObject = this.stream
        const track = this.stream.getVideoTracks()[0]
        const activeDeviceId = track && track.getSettings ? track.getSettings().deviceId : null
        await this.enumerateSources()
        if (activeDeviceId) {
          this.curCamera = activeDeviceId
        } else if (!this.curCamera && this.sources.length > 0) {
          this.curCamera = this.sources[0].deviceId
        }
        this.loading = false
      } catch (e) {
        document.body.style.overflow = ''
        this.loading = false
        if (e.name === 'NotAllowedError' || e.name === 'PermissionDeniedError') {
          this.permissionError = true
        }
        this.$emit('camera-error', e)
      }
    },
    stopStream() {
      if (this.stream) {
        this.stream.getTracks().forEach(t => t.stop())
        this.stream = null
      }
      if (this.$refs.video) {
        this.$refs.video.srcObject = null
      }
      document.body.style.overflow = ''
    },
    captureImage() {
      const video = this.$refs.video
      const canvas = this.$refs.canvas
      const doCapture = () => {
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        canvas.getContext('2d').drawImage(video, 0, 0)
        canvas.toBlob(blob => {
          this.$emit('captured', blob)
          this.closeCamera()
        }, 'image/png')
      }
      if (video.readyState >= 2) {
        doCapture()
      } else {
        video.addEventListener('canplay', doCapture, { once: true })
      }
    },
    closeCamera() {
      this.permissionError = false
      this.stopStream()
      this.$emit('close-camera', true)
    },
    async openDocument() {
      this.closeCamera()
      this.loadImageFromFile()
    },
    async onChange() {
      this.loading = true
      await this.startStream()
    }
  }
}
</script>

<style scoped>
.ic-camera {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #101010;
  padding: 0;
}

.ic-camera--header {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  background: rgba(0, 0, 0, .85);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem;
  z-index: 10;
}

.ic-camera--container {
  width: 100%;
  height: 100%;
}

.ic-camera--permission-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;
  color: #fff;
  text-align: center;
  padding: 1.5rem 2rem;
  background: rgba(0, 0, 0, .75);
  border-radius: 8px;
}

.ic-camera--footer {
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  background: rgba(0, 0, 0, .85);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 2rem;
  z-index: 10;
}

.capture {
  z-index: 10;
  width: 50px;
  height: 50px;
}

.ic-camera--header button {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ic-camera-sources {
  flex: none;
  width: 100%;
}

button i {
  top: 0;
  font-size: 1.5rem;
}

button i:before {
  color: #FFFFFF;
}

button.capture i {
  font-size: 2.5rem;
}

.select-source {
  position: relative;
}

.select-source select {
  width: 100%;
  appearance: none;
  background-image: url(data:image/svg+xml;base64,PHN2ZyBmaWxsPSdibGFjaycgaGVpZ2h0PScyNCcgdmlld0JveD0nMCAwIDI0IDI0JyB3aWR0aD0nMjQnIHhtbG5zPSdodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2Zyc+PHBhdGggZD0nTTcgMTBsNSA1IDUtNXonLz48cGF0aCBkPSdNMCAwaDI0djI0SDB6JyBmaWxsPSdub25lJy8+PC9zdmc+);
  background-repeat: no-repeat;
  background-position-x: 100%;
  background-position-y: 50%;
  border: solid 2px #FFFFFF;
  border-radius: 4px;
  color: #FFFFFF;
  padding: 5px;
  margin-bottom: 15px;
}

.select-source select:focus-visible {
  border: solid 2px #FFFFFF;
  outline: none;
}
</style>
